import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const NAV_LINKS = [
  { label: "Home", to: "/" },
  {
    label: "Inspections",
    to: "/services/pre-purchase-inspection",
    dropdown: [
      { label: "Pre-Purchase Car Inspections", to: "/services/pre-purchase-inspection" },
      { label: "Why Choose Lemon Protector", to: "/about" },
      { label: "Out-of-State Car Buyers", to: "/about#out-of-state" },
      { label: "Personal Inspector Consultation", to: "/about#consultation" },
    ],
  },
  { label: "About", to: "/about" },
  { label: "Service Areas", to: "/location/new-york" },
  { label: "Reviews", to: "/about#reviews" },
  { label: "FAQ", to: "/about#faq" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 bg-white transition-all duration-300 ${
        scrolled ? "shadow-md border-b border-gray-100" : "border-b border-gray-200"
      }`}
    >
      <nav className="flex items-center justify-between px-6 lg:px-12 h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 shrink-0">
          <div className="w-10 h-10 flex items-center justify-center">
            <img
              src="https://lemonprotector.com/images/oldsite/logo_graphic.gif"
              alt="Lemon Protector Car Logo"
              className="w-10 h-10 object-contain"
            />
          </div>
          <img
            src="https://lemonprotector.com/images/lemonpro-logo-text-1.jpg"
            alt="Lemon Protector"
            className="h-7 object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <ul className="hidden md:flex items-center gap-6">
          {NAV_LINKS.map((link) => (
            <li key={link.label} className="relative">
              {link.dropdown ? (
                <>
                  <button
                    className="flex items-center gap-1 text-xs tracking-widest uppercase font-sans font-medium text-gray-600 hover:text-brand-gold transition-colors whitespace-nowrap cursor-pointer"
                    onMouseEnter={() => setActiveDropdown(link.label)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    {link.label}
                    <i className="ri-arrow-down-s-line text-sm" />
                  </button>
                  <div
                    className={`absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-xl transition-all duration-200 ${
                      activeDropdown === link.label
                        ? "opacity-100 translate-y-0 pointer-events-auto"
                        : "opacity-0 -translate-y-2 pointer-events-none"
                    }`}
                    onMouseEnter={() => setActiveDropdown(link.label)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className="block px-5 py-3 text-sm font-sans text-gray-600 hover:text-brand-gold hover:bg-gray-50 transition-colors"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </>
              ) : (
                <Link
                  to={link.to}
                  className={`text-xs tracking-widest uppercase font-sans font-medium transition-colors whitespace-nowrap ${
                    location.pathname === link.to
                      ? "text-brand-gold"
                      : "text-gray-600 hover:text-brand-gold"
                  }`}
                >
                  {link.label}
                </Link>
              )}
            </li>
          ))}
        </ul>

        {/* Right CTA */}
        <div className="hidden md:flex items-center gap-4">
          <a
            href="tel:5169833800"
            className="text-sm font-sans text-brand-gold font-semibold whitespace-nowrap hover:text-brand-gold-muted transition-colors"
          >
            (516) 983-3800
          </a>
          <Link
            to="/contact"
            className="px-5 py-2.5 bg-brand-gold text-white text-xs tracking-widest uppercase font-sans font-semibold rounded-full hover:bg-brand-gold-muted transition-colors whitespace-nowrap cursor-pointer"
          >
            Book Inspection
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={`text-2xl ${mobileOpen ? "ri-close-line" : "ri-menu-3-line"}`} />
        </button>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`md:hidden bg-white border-t border-gray-100 overflow-hidden transition-all duration-300 ${
          mobileOpen ? "max-h-[700px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 py-4 flex flex-col gap-1">
          {NAV_LINKS.map((link) => (
            <div key={link.label}>
              <Link
                to={link.to}
                className="block py-3 text-sm font-sans text-gray-700 border-b border-gray-100 hover:text-brand-gold transition-colors"
              >
                {link.label}
              </Link>
              {link.dropdown && (
                <div className="pl-4">
                  {link.dropdown.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="block py-2 text-xs font-sans text-gray-500 hover:text-brand-gold transition-colors"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <a
            href="tel:5169833800"
            className="mt-4 text-center py-3 bg-brand-gold text-white text-sm font-sans font-semibold rounded-full whitespace-nowrap"
          >
            Call (516) 983-3800
          </a>
          <Link
            to="/contact"
            className="mt-2 text-center py-3 border border-brand-gold/50 text-brand-gold text-sm font-sans font-medium rounded-full whitespace-nowrap"
          >
            Book an Inspection
          </Link>
        </div>
      </div>
    </header>
  );
}
